/**
 * Green Tour Button Component
 * 
 * Floating button to activate Experience Mode
 * Hidden per page when user chose to hide after watching all videos
 */

import React from 'react';
import { useExperienceMode } from './ExperienceModeProvider';

interface GreenTourButtonProps {
  currentPageId: string;
  viewMode?: 'admin' | 'member';
}

export const GreenTourButton: React.FC<GreenTourButtonProps> = ({ currentPageId, viewMode = 'member' }) => {
  const { state, activateExperienceMode, hiddenPages } = useExperienceMode();
  
  // Calculate unseen spots count
  const unseenCount = state.progress 
    ? state.progress.totalSpots - state.progress.seenSpots.length 
    : 0;
  
  // In admin view: always show. In member view: hide if user chose to hide for this page
  if (viewMode !== 'admin' && hiddenPages.includes(currentPageId)) {
    return null;
  }
  
  return (
    <div 
      className="green-tour-button-wrapper"
      style={{ 
        position: 'fixed',
        right: 0,
        top: 'calc(50vh - 200px)',
        zIndex: 10010,
        isolation: 'isolate', // Create new stacking context
        willChange: 'transform', // Optimize for performance
      }}
    >
      <button
        onClick={() => activateExperienceMode(currentPageId)}
        className="green-tour-button"
        style={{
          position: 'relative',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          padding: '1rem 0.5rem',
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          border: 'none',
          borderRadius: '0.75rem 0 0 0.75rem',
          boxShadow: '0 4px 20px rgba(16, 185, 129, 0.4)',
          color: 'white',
          fontSize: '0.875rem',
          fontWeight: '600',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          filter: 'none !important', // Force no filter
          WebkitFilter: 'none !important', // Force no filter for Safari
        }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateX(-4px)';
        e.currentTarget.style.boxShadow = '0 6px 25px rgba(16, 185, 129, 0.5)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateX(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(16, 185, 129, 0.4)';
      }}
      title="Start guided tour"
      aria-label="Start Experience Mode tour"
    >
      <span style={{ transform: 'rotate(180deg)' }}>ℹ️</span>
      <span style={{ transform: 'rotate(180deg)', letterSpacing: '0.05em' }}>
        Zo werkt SuperKonnected
      </span>
      {unseenCount > 0 && (
        <span 
          style={{
            transform: 'rotate(180deg)',
            backgroundColor: '#ef4444',
            borderRadius: '9999px',
            padding: '0.125rem 0.375rem',
            fontSize: '0.75rem',
            fontWeight: '700',
          }}
        >
          {unseenCount}
        </span>
      )}
      </button>
    </div>
  );
};
